import { StandardEvents } from '@shopify/events';
const ADDON_MESSAGES = {
  base_variant: '商品の種類を確認できませんでした。もう一度選び直してからお試しください。',
  cart_failed: 'カートの内容を確認できませんでした。時間をおいて、もう一度お試しください。',
  legacy_addon: 'カートに、商品と組み合わされていない追加テーマ（__items__）があります。カートで該当する追加テーマを削除してから、このページで追加テーマを選び直してください。',
  base_ambiguous: 'カートに同じ商品が複数行に分かれて入っているため、どの商品に追加するか確認できません。カートで同じ商品を1行にまとめてから、もう一度お試しください。',
  base_quantity: 'カート内のこの商品の数量が2点以上です。カートで数量を1点にしてから、もう一度お試しください。',
  base_mismatch: 'カートに同じ商品が異なる内容で入っています。カートで削除してから、もう一度お試しください。',
  addon_quantity: '追加テーマ（__items__）がカートに2点以上入っています。カートで数量を1点にしてから、もう一度お試しください。',
  add_failed: 'カートに追加できませんでした。在庫状況とカートの内容を確認してから、もう一度お試しください。',
  nested_unverified: '追加テーマと商品が組み合わされているか確認できませんでした。カートの内容を確認し、商品と組み合わされていない追加テーマがあれば削除してから選び直してください。'
};
const cartError = (code, names = []) => Object.assign(new Error(code), { names });
const addonErrorMessage = (error, fallback) => ADDON_MESSAGES[error?.message]?.replace('__items__', () => error.names.join('、')) || fallback;
const variantOf = line => String(line.variant_id);
const comparableProperties = properties => JSON.stringify(Object.entries(properties || {})
  .filter(([, value]) => value !== null && value !== '')
  .map(([key, value]) => [key, String(value)])
  .sort(([a], [b]) => (a < b ? -1 : 1)));
const planAddonCart = ({ cart, baseId, properties, chosen, recognized }) => {
  const known = new Set(recognized.map(String));
  const orphans = cart.items.filter(line => known.has(variantOf(line)) && !line.parent_relationship);
  if (orphans.length) throw cartError('legacy_addon', orphans.map(line => line.title));
  const sameVariant = cart.items.filter(line => variantOf(line) === baseId);
  const wanted = comparableProperties(properties);
  const matches = sameVariant.filter(line => !line.parent_relationship && comparableProperties(line.properties) === wanted);
  if (matches.length > 1) throw cartError('base_ambiguous');
  if (!matches.length && sameVariant.length) throw cartError('base_mismatch');
  const base = matches[0];
  if (base && base.quantity > 1) throw cartError('base_quantity');
  const children = base ? cart.items.filter(line => line.parent_relationship?.parent_key === base.key) : [];
  const selected = chosen.map(String);
  const missing = selected.filter(addon => {
    const existing = children.filter(line => variantOf(line) === addon);
    if (existing.reduce((sum, line) => sum + line.quantity, 0) > 1) throw cartError('addon_quantity', [existing[0].title]);
    return !existing.length;
  });
  const parent = base ? { parent_line_key: base.key } : { parent_id: Number(baseId) };
  const addons = missing.map(addon => ({ id: Number(addon), quantity: 1, ...parent }));
  const items = base ? addons : [{ id: Number(baseId), quantity: 1, ...(Object.keys(properties).length ? { properties } : {}) }, ...addons];
  const onlyThisPurchase = cart.items.every(line => line === base || (children.includes(line) && selected.includes(variantOf(line))));
  return { items, addons, parentKey: base?.key, onlyThisPurchase };
};
const assertNested = (plan, baseId, added) => {
  if (!plan.addons.length) return;
  const lines = Array.isArray(added?.items) ? added.items : [];
  const parentKey = plan.parentKey || lines.find(line => variantOf(line) === baseId && !line.parent_relationship)?.key;
  const nested = parentKey && plan.addons.every(addon => lines.some(line => variantOf(line) === String(addon.id) && line.parent_relationship?.parent_key === parentKey));
  if (!nested) throw cartError('nested_unverified');
};
const addAddonsToCart = async ({ request, root, baseId, properties, chosen, recognized, buyNow }) => {
  const cartResponse = await request(`${root}cart.js`, { headers: { Accept: 'application/json' } });
  if (!cartResponse.ok) throw cartError('cart_failed');
  const plan = planAddonCart({ cart: await cartResponse.json(), baseId, properties, chosen, recognized });
  if (plan.items.length) {
    const response = await request(`${root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: plan.items }) });
    if (!response.ok) throw cartError('add_failed');
    assertNested(plan, baseId, plan.addons.length ? await response.json().catch(() => null) : null);
  }
  return buyNow ? `${root}checkout?skip_shop_pay=true` : `${root}cart`;
};
(() => {
  if (window.__odAddonsInstalled) return;
  window.__odAddonsInstalled = true;
  const states = new WeakMap();
  const initialize = () => document.querySelectorAll('[data-od-addons]').forEach(panel => {
    if (states.has(panel)) return;
    const hero = panel.closest('[data-od-product]');
    const form = hero?.querySelector('product-form-component form[data-type="add-to-cart-form"]') || panel.closest('form[action*="/cart/add"]');
    const select = panel.querySelector('[data-od-addon-select]');
    if (!hero || !form || !select || select.options.length < 2) return;
    let bases;
    try { bases = JSON.parse(panel.querySelector('[data-od-addon-bases]').textContent); } catch { return; }
    const state = { panel, hero, form, select, bases, chosen: new Map(), busy: false };
    const picker = panel.querySelector('[data-od-addon-picker]');
    const menu = panel.querySelector('[data-od-addon-menu]');
    const choices = [];
    for (const option of [...select.options].filter(option => option.value)) {
      const button = document.createElement('button');
      button.type = 'button'; button.dataset.addonId = option.value;
      const title = document.createElement('span');
      title.textContent = (option.dataset.title || option.textContent).replace(/[（(]\s*\d+日\s*[）)]\s*$/, '');
      const period = document.createElement('small'); period.textContent = option.dataset.period;
      const price = document.createElement('strong');
      price.textContent = new Intl.NumberFormat('ja-JP', { style: 'currency', currency: panel.dataset.currency || 'JPY' }).format(Number(option.dataset.price) / 100);
      button.append(title, period, price); menu.append(button); choices.push(button);
      button.addEventListener('click', () => {
        select.value = option.value; select.dispatchEvent(new Event('change', { bubbles: true }));
        picker.open = false; picker.querySelector('summary').focus();
      });
    }
    picker.hidden = false; select.hidden = true;
    picker.addEventListener('keydown', event => { if (event.key === 'Escape') { picker.open = false; picker.querySelector('summary').focus(); } });
    document.addEventListener('click', event => { if (!picker.contains(event.target)) picker.open = false; });
    states.set(panel, state);
    const status = panel.querySelector('[data-od-addon-status]');
    const money = new Intl.NumberFormat('ja-JP', { style: 'currency', currency: panel.dataset.currency || 'JPY' });
    state.render = () => {
      const list = panel.querySelector('[data-od-addon-rows]');
      list.replaceChildren();
      state.chosen.forEach((item, id) => {
        const row = document.createElement('li');
        const label = document.createElement('span');
        label.textContent = `${item.label} × 1`;
        const remove = document.createElement('button');
        remove.type = 'button'; remove.textContent = panel.dataset.removeLabel; remove.disabled = state.busy;
        remove.setAttribute('aria-label', panel.dataset.removeAria.replace('__item__', item.label));
        remove.addEventListener('click', () => { state.chosen.delete(id); state.render(); });
        row.append(label, remove); list.append(row);
      });
      [...select.options].forEach(option => { option.disabled = state.chosen.has(option.value); });
      select.disabled = state.busy;
      choices.forEach(button => { button.disabled = state.busy || state.chosen.has(button.dataset.addonId); });
      picker.querySelector('summary').setAttribute('aria-disabled', String(state.busy));
      const base = bases.find(item => String(item.id) === form.elements.id.value);
      const total = (base?.price || 0) + [...state.chosen.values()].reduce((sum, item) => sum + item.price, 0);
      panel.querySelector('[data-od-addon-total-value]').textContent = money.format(total / 100);
      panel.querySelector('[data-od-addon-total]').hidden = false;
      hero.classList.toggle('od-has-addons', !!state.chosen.size);
      const buyButton = form.querySelector('[data-od-buy-now]');
      const cartButton = form.querySelector('button[name="add"]');
      if (buyButton && cartButton) buyButton.disabled = state.busy || cartButton.disabled;
    };
    select.addEventListener('change', () => {
      const option = select.selectedOptions[0];
      if (option?.value && !state.chosen.has(option.value)) {
        state.chosen.set(option.value, { label: option.textContent, price: Number(option.dataset.price) });
        status.textContent = ''; state.render();
      }
      select.value = '';
    });
    hero.addEventListener('od:base-updated', state.render);
    hero.addEventListener(StandardEvents.productSelect, event => { state.pendingVariant = event.promise; });
    form.querySelector('[name="id"]')?.addEventListener('change', state.render);
    state.status = status;
    panel.hidden = false; state.render();
  });
  window.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('form[action*="/cart/add"]')) return;
    const panel = form.closest('[data-od-product]')?.querySelector('[data-od-addons]');
    const state = panel && states.get(panel);
    const buyNow = event.submitter?.matches('[data-od-buy-now]');
    if (!state || state.form !== form || (!state.chosen.size && !buyNow)) return;
    const scope = form.closest('[data-od-product]');
    if (!scope) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (state.busy) return;
    state.busy = true; state.status.textContent = state.panel.dataset.loadingLabel; state.render();
    const buttons = [...state.form.querySelectorAll('[type="submit"]')];
    const disabled = buttons.map(button => button.disabled);
    buttons.forEach(button => { button.disabled = true; });
    const root = window.Shopify?.routes?.root || '/';
    try {
      if (state.pendingVariant) {
        const pendingVariant = state.pendingVariant;
        try { await pendingVariant; await Promise.resolve(); }
        finally { if (state.pendingVariant === pendingVariant) state.pendingVariant = null; }
      }
      const id = form.elements.id?.value;
      if (!state.bases.some(base => String(base.id) === String(id))) throw cartError('base_variant');
      const properties = Object.fromEntries([...new FormData(form)].filter(([key]) => /^properties\[.+\]$/.test(key)).map(([key, value]) => [key.slice(11, -1), value]));
      const recognized = [...state.select.options].map(option => option.value).filter(Boolean);
      window.location.assign(await addAddonsToCart({ request: fetch, root, baseId: String(id), properties, chosen: [...state.chosen.keys()], recognized, buyNow }));
    } catch (error) {
      state.status.textContent = addonErrorMessage(error, state.panel.dataset.errorLabel);
      state.busy = false; buttons.forEach((button, index) => { button.disabled = disabled[index]; }); state.render();
    }
  }, true);
  initialize();
  document.addEventListener('shopify:section:load', initialize);
})();
